import { ImagePresenter } from "components/common/ImagePresenter";
import { VideoPresenter } from "components/common/VideoPresenter";
import { BackButton } from "components/parts/BackButton";
import { ChatArea } from "components/report/ChatArea";
import { ReportCell } from "components/report/ReportCell";
import { ReportCellTripleCol } from "components/report/ReportCellTripleCol";
import { useBasicInformationForReport } from "pages/Report/hooks";
import {
  ReportBaseDiv,
  ReportLayoutDiv,
  ReportBaseMain,
  StyledTextHeader,
  StyledCellContainer,
  StyledCellHeader,
  StyledMarginDiv,
  StyledReportContainer,
  StyledCellSecondHeader,
} from "pages/Report/styled";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import React, { useEffect } from "react";
import { ResourceType } from "types/graphqlapi";

export const Report: React.FC = () => {
  const { selectedSubjectId, setScreenName, setPageLevel } = useConsoleState();
  const {
    subjectInformation,
    fileResource,
    scenarioInformation,
    monitorInformation,
    chatRoomInformation,
  } = useBasicInformationForReport(selectedSubjectId);

  useEffect(() => {
    setScreenName("レポート");
    setPageLevel(PageLevel.fileAndScenarioAndMonitor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageResourceList = fileResource.filter(
    (resource) => resource.type === ResourceType.IMAGE
  );

  const videoResourceList = fileResource.filter(
    (resource) => resource.type === ResourceType.VIDEO
  );

  return (
    <StyledReportContainer>
      <BackButton />
      <ReportBaseMain>
        <ReportLayoutDiv>
          <ReportBaseDiv className="release-scroll">
            <StyledTextHeader>案件レポート</StyledTextHeader>
            <StyledCellContainer>
              <StyledCellHeader>案件情報</StyledCellHeader>
              <ReportCell
                label="案件ID"
                text={subjectInformation.subjectNumber}
              />
              <ReportCell
                label="案件名"
                text={subjectInformation.subjectName}
              />
              <ReportCell
                label="ルームタイプ"
                text={subjectInformation.interviewTypeDescription}
              />
              <ReportCell
                label="案件概要"
                text={subjectInformation.subjectDescription}
              />
              <ReportCell
                label="案件目的"
                text={subjectInformation.subjectPurpose}
              />
              <ReportCell
                label="アスマーク主担当"
                text={subjectInformation.mainUserAsmarq}
              />
              <ReportCell
                label="グループ主担当"
                text={subjectInformation.mainUserGroup}
              />
              <ReportCell
                label="終了予定日"
                text={subjectInformation.scheduledEndDate}
              />
              <ReportCell label="ステータス" text={subjectInformation.status} />
              <ReportCell label="備考" text={subjectInformation.memo} />
            </StyledCellContainer>
            <StyledCellContainer>
              <StyledCellHeader>インタビューフロー</StyledCellHeader>
              {scenarioInformation.map((scenario) => {
                return (
                  <ReportCell
                    key={scenario.key}
                    label={scenario.label}
                    text={scenario.value}
                  />
                );
              })}
            </StyledCellContainer>
            <StyledCellContainer>
              <StyledCellHeader>モニター情報</StyledCellHeader>
              {monitorInformation.map((monitor) => (
                <StyledMarginDiv key={monitor.ansId}>
                  <ReportCellTripleCol
                    labelA={monitor.ansId}
                    labelB="モニターID"
                    text={monitor.memberId}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="管理ID"
                    text={monitor.ansId}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="ニックネーム"
                    text={monitor.name}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="グループ / 割付"
                    text={monitor.classfication}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="性別"
                    text={monitor.sex}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="年齢"
                    text={monitor.age}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="職業"
                    text={monitor.job}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="居住地"
                    text={monitor.address}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="既婚有無"
                    text={monitor.marriage}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="子有無"
                    text={monitor.children}
                  />
                  {monitor.attribute.map((attr) => (
                    <ReportCellTripleCol
                      key={attr.seqNo}
                      labelA=""
                      labelB={attr.name}
                      text={attr.value}
                    />
                  ))}
                </StyledMarginDiv>
              ))}
            </StyledCellContainer>
            <StyledCellContainer>
              <StyledCellHeader>実査情報</StyledCellHeader>
              {chatRoomInformation.map((room) => (
                <StyledMarginDiv
                  key={room.interviewSeq}
                  className="page-break-after"
                >
                  <ReportCellTripleCol
                    labelA={`${room.interviewSeq}`}
                    labelB="予定日時"
                    text={room.scheduled}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="実施日時"
                    text={room.actual}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="参加予定モニター"
                    text={room.scheduledMonitor}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="参加モニター"
                    text={room.actualMonitor}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="備考"
                    text={room.memo}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="ステータス"
                    text={room.status}
                  />
                  <ReportCellTripleCol
                    labelA=""
                    labelB="メモ"
                    text={room.note}
                  />
                  {room.status === "実施済" && (
                    <ChatArea
                      subjectId={selectedSubjectId}
                      interviewId={room.id}
                    />
                  )}
                </StyledMarginDiv>
              ))}
            </StyledCellContainer>
            <StyledCellContainer>
              <StyledCellHeader>使用ファイル情報</StyledCellHeader>

              {imageResourceList.length > 0 && (
                <>
                  <StyledCellSecondHeader
                    text="画像"
                    textAlign="left"
                    weight="600"
                    size="large"
                  />
                  {imageResourceList.map((resource, index) => (
                    <StyledMarginDiv key={`${resource.fileName}${index}`}>
                      <ReportCell label="ファイル名" text={resource.fileName} />
                      <ReportCell label="タイトル" text={resource.title} />
                      <ReportCell label="説明" text={resource.description} />
                      <ImagePresenter
                        src={resource.url}
                        maxHeight="20%"
                        maxWidth="20%"
                      />
                    </StyledMarginDiv>
                  ))}
                </>
              )}

              {videoResourceList.length > 0 && (
                <>
                  <StyledCellSecondHeader
                    text="動画"
                    textAlign="left"
                    weight="600"
                    size="large"
                  />
                  {videoResourceList.map((resource, index) => (
                    <StyledMarginDiv key={`${resource.fileName}${index}`}>
                      <ReportCell label="ファイル名" text={resource.fileName} />
                      <ReportCell label="タイトル" text={resource.title} />
                      <ReportCell label="説明" text={resource.description} />
                      <VideoPresenter
                        src={resource.url}
                        maxHeight="20%"
                        maxWidth="20%"
                      />
                    </StyledMarginDiv>
                  ))}
                </>
              )}
            </StyledCellContainer>
          </ReportBaseDiv>
        </ReportLayoutDiv>
      </ReportBaseMain>
    </StyledReportContainer>
  );
};
